import { Calendar, MessageSquare, User } from 'lucide-react'
import { useState, useRef, useCallback } from 'react'

/**
 * 任务预览数据
 */
export interface TaskPreviewData {
  id: string
  title: string
  description: string | null
  assignees: Array<{ id: string; name: string }>
  dueDate: string | null
  recentComments: Array<{
    content: string
    authorName: string
    createdAt: string
  }>
}

export interface TaskPreviewTooltipProps {
  /** 任务预览数据 */
  task: TaskPreviewData
  /** 子元素 */
  children: React.ReactNode
  /** 显示延迟（毫秒），默认 500ms */
  showDelay?: number
  /** 隐藏延迟（毫秒），默认 200ms */
  hideDelay?: number
  /** 是否禁用 */
  disabled?: boolean
}

/**
 * 任务预览提示组件
 * 
 * 在任务标题悬停时显示任务详情预览，包括：
 * - 任务描述
 * - 负责人列表
 * - 截止日期
 * - 最近评论
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.4
 */
export function TaskPreviewTooltip({
  task,
  children,
  showDelay = 500,
  hideDelay = 200,
  disabled = false,
}: TaskPreviewTooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current)
      showTimerRef.current = null
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }
  }, [])

  const handleMouseEnter = useCallback(
    () => {
      if (disabled) {return}

      clearTimers()

      // Requirement 4.1: 500ms 延迟显示
      showTimerRef.current = setTimeout(() => {
        const rect = triggerRef.current?.getBoundingClientRect()
        if (rect) {
          setPosition({
            x: rect.left + rect.width / 2,
            y: rect.bottom + 8,
          })
        }
        setIsVisible(true)
      }, showDelay)
    },
    [disabled, showDelay, clearTimers]
  )

  const handleMouseLeave = useCallback(() => {
    clearTimers()

    // Requirement 4.4: 200ms 延迟隐藏
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false)
    }, hideDelay)
  }, [hideDelay, clearTimers])

  const handleTooltipMouseEnter = useCallback(() => {
    // 鼠标进入提示框时取消隐藏
    clearTimers()
  }, [clearTimers])

  const handleTooltipMouseLeave = useCallback(() => {
    // 鼠标离开提示框时延迟隐藏
    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false)
    }, hideDelay)
  }, [hideDelay])

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        {children}
      </div>

      {isVisible && (
        <div
          className="fixed z-50 animate-in fade-in-0 zoom-in-95"
          style={{
            left: position.x,
            top: position.y,
            transform: 'translateX(-50%)',
          }}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        >
          <TaskPreviewCard task={task} />
        </div>
      )}
    </>
  )
}

/**
 * 任务预览卡片
 * Requirement 4.2: 显示任务详情
 */
interface TaskPreviewCardProps {
  task: TaskPreviewData
}

function TaskPreviewCard({ task }: TaskPreviewCardProps) {
  return (
    <div className="w-80 rounded-lg border bg-popover p-4 shadow-lg">
      {/* 标题 */}
      <h4 className="font-medium text-foreground line-clamp-2">{task.title}</h4>

      {/* 描述 */}
      <div className="mt-2">
        {task.description ? (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {task.description}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground italic">暂无描述</p>
        )}
      </div>

      {/* 负责人 */}
      <div className="mt-3 flex items-start gap-2">
        <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
        <div className="flex-1">
          {task.assignees.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {task.assignees.map((assignee) => (
                <span
                  key={assignee.id}
                  className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs"
                >
                  {assignee.name}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground italic">未分配</span>
          )}
        </div>
      </div>

      {/* 截止日期 */}
      <div className="mt-2 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-muted-foreground" />
        {task.dueDate ? (
          <span className="text-sm">
            {new Date(task.dueDate).toLocaleDateString('zh-CN')}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground italic">无截止日期</span>
        )}
      </div>

      {/* 最近评论 */}
      {task.recentComments.length > 0 && (
        <div className="mt-3 border-t pt-3">
          <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
            <MessageSquare className="h-3 w-3" />
            最近评论
          </div>
          <div className="space-y-2">
            {task.recentComments.slice(0, 3).map((comment, index) => (
              <div key={index} className="text-xs">
                <div className="flex items-center gap-1">
                  <span className="font-medium">{comment.authorName}</span>
                  <span className="text-muted-foreground">
                    {formatRelativeTime(comment.createdAt)}
                  </span>
                </div>
                <p className="text-muted-foreground line-clamp-2 mt-0.5">
                  {comment.content}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * 格式化相对时间
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) {return '刚刚'}
  if (diffMins < 60) {return `${diffMins}分钟前`}
  if (diffHours < 24) {return `${diffHours}小时前`}
  if (diffDays < 7) {return `${diffDays}天前`}
  return date.toLocaleDateString('zh-CN')
}

/**
 * 验证任务预览数据完整性
 * 用于属性测试
 */
export function validateTaskPreviewData(task: TaskPreviewData): {
  hasDescription: boolean
  hasAssignees: boolean
  hasDueDate: boolean
  hasComments: boolean
  isComplete: boolean
} {
  const hasDescription = task.description !== null && task.description.length > 0
  const hasAssignees = task.assignees.length > 0
  const hasDueDate = task.dueDate !== null
  const hasComments = task.recentComments.length > 0

  return {
    hasDescription,
    hasAssignees,
    hasDueDate,
    hasComments,
    // 完整性：至少有描述或负责人
    isComplete: hasDescription || hasAssignees,
  }
}
